import React, {
  useContext, useCallback, useEffect, useState
}                                                     from 'react'
import {
  Avatar, Box, Button,
  Checkbox, Chip, Container,
  FormControlLabel, FormGroup,
  Grid, IconButton, Link,
  Paper, Stack,
  TextField, Typography,
}                                                     from '@mui/material'
import { Trans, useTranslation }                      from 'react-i18next'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useApi }                                     from '../utils/api'
import { useSnackbar }                                from 'notistack'
import AlertSimpleDialog                              from '../ui/components/alert-simple-dialog'
import Page                                           from '../ui/components/Page'
import { BaseBreadcrumbs }                            from '../ui/components/BaseBreadcrumbs'
import ShortFormControl                               from '../ui/components/short-form-control'
import {
  dateFromInputStringToISO, dateToInputString,
  fPrice, fVatRate, fDateTime, fmVisibleDate
}                                                     from '../utils/textutils'
import BaseTable                                      from '../ui/components/base-table'
import Iconify                                        from '../ui/components/Iconify'
import ShortFormSelect                                from '../ui/components/short-form-select'
import Consts                                         from '../Consts'
import Divider                                        from '@mui/material/Divider'
import { DateTime }                                   from 'luxon'
import ServiceBulkgateSync                            from './ServiceBulkgateSync'
import ScheduleChangeDialog                           from './ScheduleChangeDialog'
import SummaryChangesDialog                           from './SummaryChangesDialog'
import ScheduledChangeDeleteDialog                    from './ScheduledChangeDeleteDialog'
import ServiceOperatorDuplicateDialog                 from './ServiceOperatorDuplicate'
import useCategories                                  from '../hooks/useCategories'
import { AppContext }                                 from '../App'

function ServiceOperatorsTable({ service }) {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()
  const [serviceOperatorsForceReloadCounter, setServiceOperatorsForceReloadCounter] = useState(0)

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false },
    { field: 'operator', headerName: t('Operator'), flex: 1, sortable: true, valueGetter: (params) => params.row.operator?.name ?? '', filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/services/${cell.row.serviceId}/operator/${cell.row.id}`}
          >
            {cell.row.operator?.name ?? ''}
          </Link>
        )
      }},
    { field: 'keyword', headerName: t('Keyword'), flex: 1, sortable: true, filterable: false },
    { field: 'pattern', headerName: t('Pattern'), flex: 1, sortable: false, filterable: false },
    { field: 'priceWithVat', headerName: t('Price'), flex: 2, sortable: false, filterable: false, renderCell: (cell) => {
      return `${fPrice(cell.row.priceWithVat)} (${fVatRate(cell.row.vatRate)}) - ${cell.row.chargeType === 'single' ? t('chargeTypeSingle') : t('chargeTypeRecurring')}`
    } },
    { field: 'priceWithVatIncorrectAnswer', headerName: t('Price incorrect answer'), flex: 2, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.priceWithVatIncorrectAnswer ? `${fPrice(cell.row.priceWithVatIncorrectAnswer)} (${fVatRate(cell.row.vatRate)})` : '---'
      } },
    { field: 'action', headerName: '', width: 100, sortable: false, filterable: false, renderCell: (cell) => {
        return <ServiceOperatorDuplicateDialog t={t} serviceOperator={cell.row} onServiceOperatorDuplicated={(newItem) => setServiceOperatorsForceReloadCounter(prev => prev + 1)} />
      }},
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Service operators')}</Typography>
        <Box flexGrow={'1'} />
        <Button variant="contained"
                component={RouterLink}
                to={`/services/${service.id}/operator/create`}
                startIcon={<Iconify icon="material-symbols:add" />}
                disabled={user?.readonly}>
          {t('New service operator')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        forceReloadCounter={serviceOperatorsForceReloadCounter}
        sortingMode={'client'}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.serviceOperator.list(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load service operators', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Grid>
  </>

}

function ServiceSharesTable({ service }) {

  const api = useApi()
  const { t } = useTranslation()
  const { user } = useContext(AppContext)

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false, },
    { field: 'name', headerName: t('Name'), flex: 1, sortable: true, filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/services/${cell.row.serviceId}/share/${cell.row.id}`}
          >
            {cell.row.name ?? '---'}
          </Link>
        )
      }
    },
    { field: 'operator', headerName: t('Operator'), flex: 1, sortable: true, valueGetter: (params) => params.row.operator?.name ?? '---', filterable: false },
    { field: 'transactionType', headerName: t('Share type'), flex: 1, sortable: true, filterable: false, renderCell: (cell) => {
        return t(`transactionType_${cell.row.transactionType}`)
      }
    },
    { field: 'position', headerName: t('Share position'), flex: 1, sortable: false, filterable: false },
    { field: 'value', headerName: t('Share value'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        if (cell.row.variant === Consts.Share.variantPrice) {
          return fPrice(cell.row?.value ?? 0, 4)
        }
        return t('share-fm-mt-val-percent', { val: (cell.row?.value ?? 0) * 100 })
      }
    },
    { field: 'mtValue', headerName: t('Share mt value'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        if (cell.row.variant === Consts.Share.variantPrice) {
          return fPrice(cell.row?.mtValue ?? 0, 4)
        }
        return t('share-fm-mt-val-percent', { val: (cell.row?.mtValue ?? 0) * 100 })
      }
    },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Service shares')}</Typography>
        <Box flexGrow={'1'}/>
        <Button variant="contained"
                disabled={user?.readonly}
                component={RouterLink}
                to={`/services/${service.id}/share/create`}
                startIcon={<Iconify icon="material-symbols:add"/>}>
          {t('New service share')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.share.list(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load service shares', e)
          }
        }}
      />
    </Grid>
  </>
}

function ServicePeriodsTable({ service }) {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false },
    { field: 'pattern', headerName: t('Pattern'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return (
            <Link component={RouterLink} variant="body2" to={`/services/${cell.row.serviceId}/service-period/${cell.row.id}`}>
              {cell.row.pattern ?? ''}
            </Link>
        )
      }},
    { field: 'validFrom', headerName: t('Valid from'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return fDateTime(cell.row.validFrom)
      }
    },
    { field: 'validTo', headerName: t('Valid to'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return fDateTime(cell.row.validTo)
      }
    },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Service periods')}</Typography>
        <Box flexGrow={'1'} />
        <Button variant="contained"
                component={RouterLink}
                to={`/services/${service.id}/service-period/create`}
                startIcon={<Iconify icon="material-symbols:add" />}
                disabled={user?.readonly}>
          {t('New service period')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        sortingMode={'client'}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.servicePeriod.list(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load service periods', e)
            return {
              data: [],
              totalCount: 0,
            }
          }}}
      />
    </Grid>
  </>
}

function ServiceInfoAnswersTable({ service }) {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false },
    { field: 'keyword', headerName: t('Keyword'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return (
          <Link component={RouterLink} variant="body2" to={`/services/${cell.row.serviceId}/info-answer/${cell.row.id}`}>
            {cell.row.keyword ?? ''}
          </Link>
        )
    }
    },
    { field: 'related', headerName: t('Related'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return  cell.row.related ? fDateTime(cell.row.related, fmVisibleDate) : '---'
      }
    },
    { field: 'content', headerName: t('Content'), flex: 1, sortable: false, filterable: false },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Info answers')}</Typography>
        <Box flexGrow={'1'} />
        <Button variant="contained"
                component={RouterLink}
                to={`/services/${service.id}/info-answer/create`}
                startIcon={<Iconify icon="material-symbols:add" />}
                disabled={user?.readonly}>
          {t('Import info answers')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.infoAnswer.list(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load info answers', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Grid>
  </>
}

function ServiceInvestments({ service }) {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false, },
    { field: 'accounted', headerName: t('Accounted'), flex: 1, sortable: true, filterable: false, renderCell: (cell) => {
        return (
          <Link
            component={RouterLink}
            variant="body2"
            to={`/services/${cell.row.serviceId}/investments/${cell.row.id}`}
          >
            {fDateTime(cell.row.accounted, 'dd.MM.yyyy') ?? '---'}
          </Link>
        )
      }},
    { field: 'price', headerName: t('Price'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return fPrice(cell.row?.price ?? 0, 2)
      }
    },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Investments')}</Typography>
        <Box flexGrow={'1'} />
        <Button variant="contained"
                disabled={user?.readonly}
                component={RouterLink}
                to={`/services/${service.id}/investments/create`}
                startIcon={<Iconify icon="material-symbols:add"/>}>
          {t('New service investment')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        sortingMode={'client'}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.transactionShare.list(service?.id, undefined, undefined, Consts.TransactionShare.typeInvestment, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load transaction shares', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Grid>
  </>
}

function ServiceEmailTable({ service }) {

  const api = useApi()
  const { user } = useContext(AppContext)
  const { t } = useTranslation()

  const columns = [
    { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false },
    { field: 'cronExpression', headerName: t('Cron expression'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return (
          <Link component={RouterLink} variant="body2" to={`/services/${cell.row.serviceId}/email/${cell.row.id}`}>
            {cell.row.cronExpression ?? ''}
          </Link>
        )
      }
    },
    { field: 'recipients', headerName: t('Recipients'), flex: 1, sortable: false, filterable: false },
    { field: 'lastSent', headerName: t('Last sent'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
        return cell.row.lastSent ? fDateTime(cell.row.lastSent) : '---'
      }
    },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Emails')}</Typography>
        <Box flexGrow={'1'} />
        <Button variant="contained"
                component={RouterLink}
                to={`/services/${service.id}/email/create`}
                startIcon={<Iconify icon="material-symbols:add" />}
                disabled={user?.readonly}>
          {t('New service email')}
        </Button>
      </Stack>
      <BaseTable
        columns={columns}
        sortingMode={'client'}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.serviceEmail.list(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load service emails', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Grid>
  </>
}

function ServiceLogTable({ service }) {

  const api = useApi()
  const { t } = useTranslation()

  const columns = [
    { field: 'created', headerName: t('Created'), width: 140, sortable: false, filterable: false, renderCell: (cell) => fDateTime(cell.row.created) },
    { field: 'action', headerName: t('Log action'), flex: 1, sortable: false, filterable: false },
    { field: 'message', headerName: t('Log message'), flex: 2, sortable: false, filterable: false },
  ]

  return <>
    <Grid item xs={12}>
      <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
        <Typography variant="h6" gutterBottom>{t('Logs')}</Typography>
      </Stack>
      <BaseTable
        columns={columns}
        sortingMode={'client'}
        loadCallback={async (query, page, pageSize) => {
          try {
            return await api.log.serviceList(service?.id, query, page, pageSize)
          } catch (e) {
            console.log('Failed to load service logs', e)
            return {
              data: [],
              totalCount: 0,
            }
          }
        }}
      />
    </Grid>
  </>
}

const getDays = ({ t }) => [
  { day: 'monday', title: t('Monday'), items: [] },
  { day: 'tuesday', title: t('Tuesday'), items: [] },
  { day: 'wednesday', title: t('Wednesday'), items: [] },
  { day: 'thursday', title: t('Thursday'), items: [] },
  { day: 'friday', title: t('Friday'), items: [] },
  { day: 'saturday', title: t('Saturday'), items: [] },
  { day: 'sunday', title: t('Sunday'), items: [] }
]

const TimeAvailabilityItem = ({ day, onItemsChanged }) => {

  const [times, setTimes] = useState(day.items ?? [])
  const { t } = useTranslation()

  useEffect(() => {
    onItemsChanged(times)
  }, [times])

  return <>
    <Stack direction={'column'} spacing={2}>
      <Divider sx={{ my: 2 }}>
        <Chip label={day.title} size="small"/>
      </Divider>
      {times.map((it, itIndex) => {
        return <Stack direction={'row'} spacing={1} alignItems={'flex-end'} key={`list-item-${itIndex}`}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.light' }}>{itIndex + 1}</Avatar>
          <TextField
            id={`time-${day.id}-${itIndex}-from`}
            label={t('From')}
            variant={'standard'}
            type="time"
            value={it.from ?? ''}
            onChange={(e) => {
              setTimes((prev) => {
                prev[itIndex].from = e.target.value
                return [...prev]
              })
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 1,
            }}
            sx={{ width: 110 }}
          />
          <TextField
            id={`time-${day.id}-${itIndex}-to`}
            label={t('To')}
            variant={'standard'}
            type="time"
            value={it.to ?? ''}
            onChange={(e) => {
              setTimes((prev) => {
                prev[itIndex].to = e.target.value
                return [...prev]
              })
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 1,
            }}
            sx={{ width: 110 }}
          />
          <IconButton edge="end" onClick={(e) => {
            setTimes((prev) => prev.filter((val, filterIndex) => filterIndex !== itIndex))
          }}>
            <Iconify icon={'material-symbols:delete'}/>
          </IconButton>
        </Stack>
      })}
      <Button variant="outlined" onClick={(e) => {
        setTimes((prev) => [...prev, { from: null, to: null }])
      }}>
        <Iconify icon={'material-symbols:add'}/>
      </Button>
    </Stack>
  </>
}
const TimeAvailability = ({ availability, onChange }) => {

  const { t } = useTranslation()
  const mergeDays = (input = []) => {
    let days = getDays({ t })
    days.forEach(day => {
      day.items = (input ?? []).filter(it => it.day === day.day)
    })
    return days
  }

  const [items, setItems] = useState(mergeDays(availability))

  const onChangeCallback = useCallback(() => {
    onChange(items.flatMap(it => it.items.map(time => ({ ...time, day: it.day }))))
  }, [items])

  return (<>
    <Typography variant={'h6'} sx={{ my: 2, color: 'text.secondary' }}>{t('Time availability')}</Typography>
    <Stack direction={'row'} spacing={2} useFlexGap flexWrap="wrap"
           divider={<Divider orientation="vertical" flexItem/>}>
      {items.map((day, index) => (
        <TimeAvailabilityItem key={`time-availability-${index}`} day={day} onItemsChanged={(items) => {
          setItems((prev) => {
            prev[index].items = items
            return [...prev]
          })
          onChangeCallback()
        }}/>))}
    </Stack>
  </>)
}

export default function ServicePage() {

  const api = useApi()
  const { user } = useContext(AppContext)
  const navigate = useNavigate()
  const { serviceId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [categories] = useCategories()
  const [partners, setPartners] = useState([])
  const [values, setValues] = useState({
    title: '',
    type: '',
    connectionType: '',
    phone: '',
    pattern: '',
    activeFrom: dateToInputString(DateTime.now().toISO()),
    activeTo: dateToInputString(DateTime.now().plus({ years: 100 }).toISO()),
    smsActivation: '',
    smsDeactivation: '',
    smsRecPayment: '',
    smsUnavailable: '',
    smsOnActiveSubscription: '',
    smsCorrectAnswer: '',
    smsIncorrectAnswer: '',
    activeSubscriptionPeriod: '',
    smsReminder: '',
    smsOutOfOperatingHours: '',
    smsOperatorNotDefined: '',
    smsInvalidInputCode: '',
    smsOutOfPeriod: '',
    smsOnMsgContentSaved: '',
    smsInfoAnswerNotConfigured: '',
    smsServicePeriodNotConfigured: '',
    partnerId: '',
    categoryId: '',
    availability: [],
    requireInputCode: false,
    syncBulkgate: false,
    bulkgateDefs: [],
    shares: [],
    archived: false,
    pickAnswerByDate: false,
    allowSameAnswer: false,
    syncEmail: false,
    syncEmailRecipient: '',
    syncEmailSubject: '',
    syncEmailText: ''
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)
  const [forceReloadChangesCounter, setForceReloadChangesCounter] = useState(0)

  useEffect(() => {
    api.partner.list(null, 0, 1000).then(({ data }) => setPartners(data))
  }, [])

  useEffect(() => {
    (async () => {
      if (!isNaN(serviceId)) {
        try {
          let service = await api.service.detail(serviceId)
          setValues({
            ...service,
            activeFrom: dateToInputString(service.activeFrom),
            activeTo: dateToInputString(service.activeTo),
            activeSubscriptionPeriod: service.activeSubscriptionPeriod ? Number(service.activeSubscriptionPeriod) / (24 * 60 * 60) : '', // convert seconds to days
          })
        } catch (e){
          enqueueSnackbar(t('Can\'t load service'), {
            variant: "error"
          })
        }
      }
    })()
  }, [serviceId])

  const handleChange = (prop) => (event) => {
    setValues((prev) => ({
      ...prev,
      [prop]: event.target.value
    }))
  }

  const handleChangeDirect = (prop, value) => {
    setValues((prev) => ({
      ...prev,
      [prop]: value
    }))
  }

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.service.delete(serviceId).then(() => {
        enqueueSnackbar(t('Service deleted'), {
          variant: 'success'
        })
        navigate('/services')
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service'), {
          variant: 'error'
        })
      })
    }
  }

  const prepareFormData = () => {
    let data = {
      ...values,
    }
    if (data.activeSubscriptionPeriod) {
      data.activeSubscriptionPeriod = Number(data.activeSubscriptionPeriod) * 24 * 60 * 60 // convert days to seconds
    }
    return data
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    let data = {
      ...values,
      activeFrom: dateFromInputStringToISO(values.activeFrom),
      activeTo: dateFromInputStringToISO(values.activeTo),
    }
    if (data.activeSubscriptionPeriod) {
      data.activeSubscriptionPeriod = Number(data.activeSubscriptionPeriod) * 24 * 60 * 60 // convert days to seconds
    }
    if (!data.syncEmail) {
      data.syncEmailRecipient = '';
      data.syncEmailSubject = ''
      data.syncEmailText = ''
    }
    try {
      let service = await api.service.save(data)
      navigate(`/services/${service.id}`)
      enqueueSnackbar(t('Service saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service'), {
        variant: 'error'
      })
    }
  }

  const ScheduledChanges = ({ serviceId, setForceReloadChangesCounter, forceReloadChangesCounter, t }) => {

    const api = useApi()

    const changesColumns = [
      {
        field: 'scheduled', headerName: t('Scheduled'), flex: 1, sortable: false, filterable: false,
        renderCell: (cell) => {
          return cell.row.scheduled ? fDateTime(cell.row.scheduled) : '---'
        }
      },
      {
        field: 'executed', headerName: t('Executed'), flex: 1, sortable: false, filterable: false,
        renderCell: (cell) => {
          return cell.row.executed ? fDateTime(cell.row.executed) : '---'
        }
      },
      { field: 'id', headerName: '#', flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
          return <>
            <SummaryChangesDialog object={cell.row?.originalData} changedObject={cell.row?.changedData} />
            {!cell.row.executed && <ScheduledChangeDeleteDialog change={cell.row} onResult={(res) => {
              if (res) {
                setForceReloadChangesCounter((prev) => prev + 1)
              }}} />}
          </>
        }},
    ]

    return <React.Fragment>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h4" gutterBottom>{t('Scheduled changes')}</Typography>
      </Stack>
      <BaseTable
          columns={changesColumns}
          query={null}
          forceReloadCounter={forceReloadChangesCounter}
          loadCallback={async (query, page, pageSize) => {
            try {
              return await api.scheduleChange.service.list(serviceId)
            } catch (e) {
              console.error('Failed to load scheduled changes for service', e)
              return {
                data: [],
                totalCount: 0,
              }
            }
          }}
      />
    </React.Fragment>
  }

  const smsTypedFields = {
    [Consts.Service.typePayment]: ['smsActivation', 'smsDeactivation', 'smsOnActiveSubscription', 'smsRecPayment', 'smsOutOfOperatingHours', 'smsReminder'],
    [Consts.Service.typeDigitalAccess]: ['smsActivation', 'smsDeactivation', 'smsOnActiveSubscription', 'smsRecPayment', 'smsOutOfOperatingHours', 'smsReminder', 'smsInvalidInputCode'],
    [Consts.Service.typeCrossword]: ['smsServicePeriodNotConfigured', 'smsCorrectAnswer', 'smsIncorrectAnswer', 'smsOutOfPeriod'],
    [Consts.Service.typeContest]: ['smsServicePeriodNotConfigured', 'smsCorrectAnswer', 'smsIncorrectAnswer', 'smsOutOfPeriod'],
    [Consts.Service.typeMessage]: ['smsOnMsgContentSaved'],
    [Consts.Service.typeInfo]: ['smsInfoAnswerNotConfigured'],
  }

  const smsFields = [
    'smsUnavailable',
    'smsOperatorNotDefined',
    ...(smsTypedFields[values.type] ?? [])
  ]

  const showExtendedFeatures = !isNaN(serviceId) && [Consts.Service.typeCrossword, Consts.Service.typeContest].includes(values.type)

  return (
    <Page title={t('Service')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumb-service'}
                         title={values?.title ?? t('Service')}
                         items={[
                           { title: t('Services'), href: `/services` }
                         ]}/>
        <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
          <Paper sx={{ p: 2, width: '100%' }}>
            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12} md={8}>
                <ShortFormControl name={'title'} title={t('Title')} value={values.title} handleChange={handleChange}/>
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'type'} title={t('Service type')} handleChange={handleChange} value={values.type} items={[
                  {id: Consts.Service.typePayment, name: t('service_type-payment')},
                  {id: Consts.Service.typeDigitalAccess, name: t('service_type-digitalAccess')},
                  {id: Consts.Service.typeCrossword, name: t('service_type-crossword')},
                  {id: Consts.Service.typeContest, name: t('service_type-contest')},
                  {id: Consts.Service.typeMessage, name: t('service_type-message')},
                  {id: Consts.Service.typeInfo, name: t('service_type-info')},
                ]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormSelect name={'connectionType'} title={t('Connection type')} handleChange={handleChange} value={values.connectionType} items={[
                  {id: Consts.Service.connectionTypeMicroPayments, name: t('connectionTypeMicroPayments')},
                  {id: Consts.Service.connectionTypePremium, name: t('connectionTypePremium')}
                ]} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ShortFormControl name={'phone'} title={t('Phone')} value={values.phone} handleChange={handleChange}/>
              </Grid>
              <Grid item xs={12} md={8}>
                <ShortFormControl name={'pattern'} title={t('Pattern')} value={values.pattern} handleChange={handleChange}/>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <ShortFormControl
                    name={'activeFrom'}
                    title={t('Active from')}
                    value={values.activeFrom}
                    handleChange={handleChange}
                    inputProps={{
                      type: 'datetime-local',
                    }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <ShortFormControl
                    name={'activeTo'}
                    title={t('Active to')}
                    value={values.activeTo}
                    handleChange={handleChange}
                    inputProps={{
                      type: 'datetime-local',
                    }}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel control={<Checkbox/>} checked={values.archived} onChange={(e) => {
                    handleChangeDirect('archived', e.target.checked)
                  }} label={t('Archived')}/>
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                {partners?.length > 0 && <>
                  <ShortFormSelect name={'partnerId'} title={t('Partner')} handleChange={handleChange} value={values.partnerId} items={partners} />
                </>}
              </Grid>
              <Grid item xs={12}>
                {categories?.length > 0 && <>
                  <ShortFormSelect name={'categoryId'} title={t('Category')} handleChange={handleChange} value={values.categoryId} items={categories} />
                </>}
              </Grid>
              {[Consts.Service.typePayment, Consts.Service.typeDigitalAccess].includes(values.type) &&
                <Grid item xs={12}>
                  <TextField
                      name={'activeSubscriptionPeriod'}
                      label={t('activeSubscriptionPeriod')}
                      fullWidth={true}
                      variant={'standard'}
                      type={'number'}
                      value={values.activeSubscriptionPeriod ?? ''}
                      onChange={(e) => handleChangeDirect('activeSubscriptionPeriod', e.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>
              }
              <Grid item xs={12}>
                <Typography variant={'h6'}>{t('SMS texts')}</Typography>
              </Grid>
              {smsFields.map(key => <Grid key={key} item xs={12}>
                <ShortFormControl name={key} title={t(key)} handleChange={handleChange} value={values[key] ?? ''} multiline maxRows={4} />
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <Typography variant={'caption'}>
                    <Trans i18nKey={'sms-text-length'} values={{ length: values[key]?.length ?? 0 }} />
                  </Typography>
                  <Typography variant={'caption'}>{t(`helper-${key}`)}</Typography>
                </Stack>
              </Grid>)}
              <Grid item xs={12} sx={{ mb: 2 }}>
                {values.id && [Consts.Service.typePayment, Consts.Service.typeDigitalAccess].includes(values.type) && <TimeAvailability availability={values.availability} onChange={(val) => handleChangeDirect('availability', val)}/>}
              </Grid>
            </Grid>
            {values.type === Consts.Service.typeDigitalAccess &&
              <>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel control={<Checkbox/>} checked={values.requireInputCode} onChange={(e) => {
                      handleChangeDirect('requireInputCode', e.target.checked)
                    }} label={t('Require input code')}/>
                  </FormGroup>
                </Grid>
              </>
            }
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Checkbox/>} checked={values.syncBulkgate} onChange={(e) => {
                  handleChangeDirect('syncBulkgate', e.target.checked)
                }} label={t('Send other SMS with Bulkgate')}/>
              </FormGroup>
            </Grid>
            {values.id && values.syncBulkgate && <Grid item xs={12}>
              <ServiceBulkgateSync defaultItems={values.bulkgateDefs} onChange={(data) => handleChangeDirect('bulkgateDefs', data)} />
            </Grid>}
            {values.type === Consts.Service.typeInfo &&
                <>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox/>} checked={values.pickAnswerByDate} onChange={(e) => {
                        handleChangeDirect('pickAnswerByDate', e.target.checked)
                      }} label={t('Pick answer by current date')}/>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel control={<Checkbox/>} checked={values.allowSameAnswer} onChange={(e) => {
                        handleChangeDirect('allowSameAnswer', e.target.checked)
                      }} label={t('Allow to send same answer multiple times')}/>
                    </FormGroup>
                  </Grid>
                </>
            }
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Checkbox/>} checked={values.syncEmail} onChange={(e) => {
                  handleChangeDirect('syncEmail', e.target.checked)
                }} label={t('Email notification on SMS delivery')}/>
              </FormGroup>
            </Grid>
            {values.id && values.syncEmail && <>
              <Grid item xs={12}>
                <ShortFormControl
                  name={"syncEmailRecipient"}
                  title={t("syncEmailRecipient")}
                  value={values.syncEmailRecipient}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <ShortFormControl
                  name={"syncEmailSubject"}
                  title={t("syncEmailSubject")}
                  value={values.syncEmailSubject}
                  handleChange={handleChange}
                />
                <Stack direction={'row'} justifyContent={'end'}>
                  <Typography variant={'caption'}>{t(`helper-syncEmailSubject`)}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <ShortFormControl
                  name={"syncEmailText"}
                  title={t("syncEmailText")}
                  value={values.syncEmailText}
                  handleChange={handleChange}
                />
                <Stack direction={'row'} justifyContent={'end'}>
                  <Typography variant={'caption'}>{t(`helper-syncEmailText`)}</Typography>
                </Stack>
              </Grid>
            </>}
          </Paper>
          <Stack direction={'row'} sx={{ mt: 2, px: 1 }}>
            <Button type={'submit'} variant={'contained'} disabled={user?.readonly}>{t('Save')}</Button>
            {!isNaN(serviceId) && <>
              <ScheduleChangeDialog getFormData={() => prepareFormData()} model={Consts.ScheduleChange.modelService} modelId={Number(serviceId)} onResult={(res) => {
                if (res) {
                  setForceReloadChangesCounter((prev) => prev + 1)
                }
              }} />
            </>
            }
            { !isNaN(serviceId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} disabled={user?.readonly} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
            <Box flex={1} />
            {(showExtendedFeatures || values.type === Consts.Service.typeMessage || values.type === Consts.Service.typeInfo) && <Button component={RouterLink} to={`/services/${serviceId}/message-input/export`}>{t('Export customer answers')}</Button>}
            {values.type === Consts.Service.typeDigitalAccess && !isNaN(serviceId) && <Button component={RouterLink} to={`/services/${serviceId}/digital-access`}>{t('Digital access codes')}</Button>}
          </Stack>
        </Grid>
        {values?.id && <ServiceOperatorsTable service={values} />}
        {values?.id && <ServiceSharesTable service={values} />}
        {showExtendedFeatures && <ServicePeriodsTable service={values} />}
        {showExtendedFeatures && <ServiceEmailTable service={values} />}
        {values.type === Consts.Service.typeInfo && <ServiceInfoAnswersTable service={values} />}
        {values?.id && <ServiceInvestments service={values} />}
        <AlertSimpleDialog
          title={t('Delete service')}
          message={t('delete-confirm-msg', { name: values.name })}
          open={shownDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
        {!isNaN(serviceId) && <Grid item mt={4} xs={12}>
          <ScheduledChanges
              serviceId={serviceId}
              setForceReloadChangesCounter={setForceReloadChangesCounter}
              forceReloadChangesCounter={forceReloadChangesCounter}
              t={t}
          />
        </Grid>}
        {values?.id && <ServiceLogTable service={values} />}
      </Container>
    </Page>
  )
}